import { localized } from "@msuite/ui/localization";
import { moment } from "@msuite/utils/date";
import { createColumnHelper } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { guardString } from "@msuite/utils/strings";
import { getSuborderNumberString, getTransportOrderNumberString } from "@msuite/utils/stock";
import type { Enums, TransportOrderListItem } from "@msuite/utils/types";

const columnHelper = createColumnHelper<TransportOrderListItem>();

/** The colour each state carries, so a table scanned by eye reads as a queue draining. */
const STATE_COLORS: Record<Enums<"transportOrderState">, "amber" | "blue" | "emerald" | "zinc"> = {
	pending: "amber",
	"in-transit": "blue",
	complete: "emerald",
	canceled: "zinc",
};

export const transportOrdersTableColumns = [
	columnHelper.accessor("number", {
		id: "number",
		header: localized("stock.transport_order"),
		cell: (cell) => getTransportOrderNumberString(cell.getValue()),
	}),
	columnHelper.accessor("state", {
		id: "state",
		header: localized("general.state"),
		cell: (cell) => (
			<Tag
				color={STATE_COLORS[cell.getValue()]}
				variant="plain"
			>
				{localized(`stock.transport_order_state.${cell.getValue()}`)}
			</Tag>
		),
	}),
	// The carrier and its position rather than the unit's id: nobody reads a uuid off a screen,
	// and "Gitterbox 3 / 18" is what is printed on the box standing in front of them.
	//
	// One column for both shapes, because a leg carries one thing and the anchor says which: a
	// put-away has no unit and no box of a round, so it names the trolley the material came back
	// on — and says so plainly when it came back on none, which is a state and not a gap.
	columnHelper.accessor("loadCarrierName", {
		id: "loadCarrierName",
		header: localized("stock.transport_carrier"),
		cell: (cell) => {
			const leg = cell.row.original;
			if (leg.returnOrderId) {
				return leg.transferCarrierName ?? localized("stock.put_away_carried_by_hand");
			}
			return `${guardString(cell.getValue())} ${leg.unitNumber} / ${leg.unitTotal}`;
		},
	}),
	columnHelper.accessor("fromZoneName", {
		id: "fromZoneName",
		header: localized("stock.transport_from_zone"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("toZoneName", {
		id: "toZoneName",
		header: localized("stock.transport_to_zone"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("orderNumber", {
		id: "orderNumber",
		header: localized("stock.suborder"),
		cell: (cell) =>
			getSuborderNumberString({
				orderNumber: cell.getValue(),
				suborderNumber: cell.row.original.suborderNumber,
			}),
	}),
	columnHelper.accessor("projectName", {
		id: "projectName",
		header: localized("projects.project"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("pickedUpByName", {
		id: "pickedUpByName",
		header: localized("stock.transport_picked_up_by"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	// Beside `pickedUpByName` and not instead of it, because after a handover the two are
	// different people — the column was on the type from the start and rendered nowhere, so the
	// table showed one driver's name against the other's timestamp.
	columnHelper.accessor("completedByName", {
		id: "completedByName",
		header: localized("stock.transport_delivered_by"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor("completedAt", {
		id: "completedAt",
		header: localized("stock.transport_delivered"),
		cell: (cell) => (cell.getValue() ? moment(cell.getValue()).shortFormat() : guardString(null)),
	}),
];
